import React from 'react';

/**
 * Tailwind tabanlı, tutarlı bir skeleton sistemi.
 * Tüm yükleme durumlarında kullanılacak.
 */

interface SkeletonProps {
  className?: string;
}

// Temel skeleton — başka tüm skeletonların atomu
export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div
    className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded-md ${className}`}
    style={{ animation: 'shimmer 1.6s infinite linear' }}
  />
);

// Stat kartı (Dashboard üst kartlar)
export const SkeletonStatCard: React.FC = () => (
  <div className="dribbble-panel p-5 flex items-center gap-4">
    <Skeleton className="w-12 h-12 rounded-xl" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-2 w-24" />
    </div>
  </div>
);

// Tablo satırı
export const SkeletonTableRow: React.FC<{ cols?: number }> = ({ cols = 6 }) => (
  <tr className="border-b border-gray-100">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full max-w-[120px]" />
      </td>
    ))}
  </tr>
);

// Kart (genel amaçlı)
export const SkeletonCard: React.FC = () => (
  <div className="dribbble-panel p-5 space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
    <Skeleton className="h-3 w-full" />
    <Skeleton className="h-3 w-3/4" />
  </div>
);

// Liste öğesi (avatarli)
export const SkeletonListItem: React.FC = () => (
  <div className="flex items-center gap-3 p-3">
    <Skeleton className="w-10 h-10 rounded-full shrink-0" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-2 w-1/3" />
    </div>
    <Skeleton className="h-3 w-12 shrink-0" />
  </div>
);

// Tablo (komple)
export const SkeletonTable: React.FC<{ rows?: number; cols?: number }> = ({ rows = 5, cols = 6 }) => (
  <div className="card overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <Skeleton className="h-3 w-20" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Dashboard komple skeleton
export const SkeletonDashboard: React.FC = () => (
  <div className="page-container py-6 space-y-5">
    {/* Header */}
    <div className="flex justify-between items-center mb-4">
      <div className="space-y-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-8 w-24 rounded-xl" />
    </div>

    {/* Stat cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonStatCard key={i} />)}
    </div>

    {/* Charts row */}
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 dribbble-panel p-5 space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-48 w-full" />
      </div>
      <div className="lg:col-span-2 dribbble-panel p-5 space-y-3">
        <Skeleton className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => <SkeletonListItem key={i} />)}
      </div>
    </div>

    {/* Bottom row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SkeletonCard />
      <SkeletonCard />
    </div>
  </div>
);

// Liste sayfa skeleton (Pets, Owners, vb)
export const SkeletonListPage: React.FC = () => (
  <div className="page-container py-6 space-y-5">
    <div className="flex justify-between items-center mb-4">
      <div className="space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      <Skeleton className="h-8 w-28 rounded-xl" />
    </div>
    <div className="card p-3">
      <Skeleton className="h-10 w-full max-w-md" />
    </div>
    <SkeletonTable rows={6} cols={6} />
  </div>
);
